import * as React from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useParams } from 'react-router'
import { Loader2 } from 'lucide-react'
import { signingTokenParamSchema } from '@lib/validators/params'
import { qaKeys } from '@/hooks/queryKeys'
import { useQaSession } from '@/hooks/useQaSession'
import { useQuestionSession } from '@/hooks/useQuestionSession'
import { signService } from '@/services/SignService'
import { Button } from '@/components/ui/button'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import { PacketViewer } from '@/signing/PacketViewer'
import { QuestionSidebar } from '@/signing/QuestionSidebar'
import {
  DocumentToc,
  SignShell,
  SignStatus,
  requestErrorMessage,
} from '@/signing/SignChrome'
import { useLightOnly } from '@/theme/ThemeProvider'
import { useBrandLook } from '@/signing/brand-look'

export function QaPage() {
  useLightOnly()
  const { token: rawToken } = useParams()
  const parsedToken = signingTokenParamSchema.safeParse(rawToken)
  const token = parsedToken.success ? parsedToken.data : undefined
  const queryClient = useQueryClient()
  const sessionQuery = useQaSession(token)
  const [scrollToDocumentId, setScrollToDocumentId] = React.useState<string | undefined>()

  const session = sessionQuery.data
  useBrandLook(session?.brand)
  const question = useQuestionSession({
    token,
    source: 'qa',
    enabled: Boolean(token && session && session.gate === 'ready'),
    canAsk: false,
  })

  const loadDocument = React.useCallback(
    (documentId: string) => {
      if (!token) {
        return Promise.reject(new Error('Missing question token'))
      }
      return signService.getDocument(token, documentId, 'qa')
    },
    [token],
  )
  const loadAsset = React.useCallback(
    (documentId: string, assetId: string) => {
      if (!token) {
        return Promise.reject(new Error('Missing question token'))
      }
      return signService.getAsset(token, documentId, assetId, 'qa')
    },
    [token],
  )

  const consentMutation = useMutation({
    mutationFn: () => {
      if (!token) {
        throw new Error('Missing question token')
      }
      return signService.acceptQaConsent(token)
    },
    onSuccess: (data) => {
      if (token) {
        queryClient.setQueryData(qaKeys.session(token), data)
      }
    },
  })

  if (!token) {
    return <SignStatus title="Invalid link" body="This question link is not valid." />
  }

  if (sessionQuery.isLoading) {
    return (
      <div className="flex min-h-svh items-center justify-center bg-background">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    )
  }

  if (sessionQuery.isError || !session) {
    return (
      <SignStatus
        title="Link not found"
        body={
          sessionQuery.error
            ? requestErrorMessage(sessionQuery.error)
            : 'This question link is invalid or has expired.'
        }
      />
    )
  }

  if (session.gate === 'unavailable') {
    return (
      <SignStatus
        brand={session.brand.displayName}
        logoSrc={session.brand.logoSrc}
        accentColor={session.brand.accentColor}
        title="This request is closed"
        body={session.unavailableReason}
      />
    )
  }

  if (session.gate === 'consent') {
    return (
      <SignShell
        brand={session.brand.displayName}
        logoSrc={session.brand.logoSrc}
        accentColor={session.brand.accentColor}
        title={session.envelope.title}
        test={session.envelope.mode === 'test'}
      >
        <Card className="mx-auto max-w-xl">
          <CardHeader>
            <CardTitle>Review and consent</CardTitle>
            <CardDescription>
              Hi {session.answerer.name}. Before you can view {session.envelope.title}{' '}
              and reply to questions, please agree to review these records
              electronically.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="max-h-64 overflow-y-auto rounded-md border border-border bg-muted/40 p-4 text-sm leading-6 text-foreground whitespace-pre-wrap">
              {session.consentText}
            </div>
            {consentMutation.isError ? (
              <p className="text-sm text-destructive">
                {requestErrorMessage(consentMutation.error)}
              </p>
            ) : null}
            <Button
              disabled={consentMutation.isPending}
              onClick={() => consentMutation.mutate()}
            >
              {consentMutation.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : null}
              I agree — continue
            </Button>
          </CardContent>
        </Card>
      </SignShell>
    )
  }

  return (
    <SignShell
      brand={session.brand.displayName}
      logoSrc={session.brand.logoSrc}
      accentColor={session.brand.accentColor}
      title={session.envelope.title}
      test={session.envelope.mode === 'test'}
    >
      <div className="mx-auto grid max-w-6xl gap-6 lg:grid-cols-[minmax(0,1fr)_18rem]">
        <PacketViewer
          documents={session.documents}
          loadDocument={loadDocument}
          loadAsset={loadAsset}
          questions={{
            threads: session.threads,
            draft: question.draft,
            selectedThreadId: question.selectedThreadId,
            onSelectThread: question.onSelectThread,
          }}
          {...(scrollToDocumentId ? { scrollToDocumentId } : {})}
        />
        <div className="h-fit space-y-6 lg:sticky lg:top-6">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Documents</CardTitle>
              <CardDescription>{session.answerer.name}</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {session.documents.length > 1 ? (
                <DocumentToc
                  documents={session.documents}
                  onSelect={setScrollToDocumentId}
                />
              ) : null}
              {question.error ? (
                <p className="text-sm text-destructive">{question.error}</p>
              ) : null}
            </CardContent>
          </Card>
          <QuestionSidebar
            role="answerer"
            threads={session.threads}
            selectedThreadId={question.selectedThreadId}
            draft={null}
            composer={question.composer}
            pending={question.pending}
            onComposerChange={question.onComposerChange}
            onReply={question.onReply}
            onCancelDraft={question.onCancelDraft}
            onSelectThread={question.onSelectThread}
            viewers={session.viewers}
          />
        </div>
      </div>
    </SignShell>
  )
}
